import { zodResolver } from "@hookform/resolvers/zod";
import i18next from "i18next";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  JOURNAL_LINE_ACCOUNT_TYPE,
  type JournalAccount,
} from "@/apis/gql/types";
import { TextInput } from "@/components/shared/form/text-input";
import { Button } from "@/components/ui/button";
import { ErrorMessage } from "@/components/ui/error";
import { Form } from "@/components/ui/form";
import { LoadingSpinner } from "@/components/ui/spinner";
import { AccountEntityType } from "@/lib/constants/account-types";
import { formatIban } from "@/lib/utils";
import {
  type GetAllBankAccountsBankAccount,
  useGetAllBankAccounts,
} from "@/queries/bank-accounts/get-all";
import { useCreateJournalAccount } from "@/queries/ledger/accounts/create";
import { useUpdateJournalAccounts } from "@/queries/ledger/accounts/update";
import { ComboBoxInput } from "./combo-box-input";

const formSchema = z.object({
  journalAccountId: z.string().min(1).max(255).optional(),
  number: z
    .string()
    .min(2, { message: i18next.t("accounts.validation.account_number_min") })
    .max(50, { message: i18next.t("accounts.validation.account_number_max") }),
  name: z
    .string()
    .min(2, { message: i18next.t("accounts.validation.account_name_min") })
    .max(120, { message: i18next.t("accounts.validation.account_name_max") }),
  entityId: z.string().min(1).max(255),
});

type FormValues = z.infer<typeof formSchema>;

type Props = {
  account?: Partial<JournalAccount>;
  disabled?: {
    entityId?: boolean;
    number?: boolean;
    name?: boolean;
  };
  onSuccess?: (data: JournalAccount[] | JournalAccount) => void;
};

export const BankAccountJournalAccountForm = ({
  account,
  disabled = {},
  onSuccess,
}: Props) => {
  const { t } = useTranslation();

  const [updateAccount] = useUpdateJournalAccounts();
  const [createAccount] = useCreateJournalAccount();

  const { bankAccounts, loading, error, refetch } = useGetAllBankAccounts({
    fetchPolicy: "network-only",
  });

  const handleSubmit = async (data: FormValues) => {
    if (account?.journalAccountId) {
      const { journalAccountId, ...updateFields } = data;

      await updateAccount(
        {
          journalAccountIds: [account.journalAccountId],
          ...updateFields,
        },
        onSuccess,
      );
    } else {
      await createAccount(
        {
          name: data.name,
          number: data.number,
          entityId: data.entityId,
          entityType: AccountEntityType.BANK_ACCOUNT,
          accountType: JOURNAL_LINE_ACCOUNT_TYPE.ASSET,
        },
        onSuccess,
      );
    }
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      number: account?.number ?? "",
      name: account?.name ?? "",
      entityId: account?.entityId ?? "",
    },
  });

  if (loading) {
    return (
      <div className="flex-1 flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex justify-center items-center h-full">
        <ErrorMessage retry={refetch} />
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-2">
        <TextInput
          name="name"
          disabled={Boolean(disabled.name)}
          label={t("name")}
        />
        <TextInput
          name="number"
          disabled={Boolean(disabled.number)}
          label={t("account_number")}
        />
        <BankAccountOptions
          bankAccounts={bankAccounts}
          disabled={Boolean(disabled.entityId)}
        />
        <div className="flex justify-end mt-2">
          <Button type="submit">{t("save")}</Button>
        </div>
      </form>
    </Form>
  );
};

const BankAccountOptions = ({
  bankAccounts,
  disabled,
}: { bankAccounts: GetAllBankAccountsBankAccount[]; disabled: boolean }) => {
  const { t } = useTranslation();

  const options = useMemo(() => {
    return bankAccounts.map((bankAccount) => ({
      label: bankAccount.bankConnection?.bankName
        ? `${bankAccount.bankConnection.bankName} - ${bankAccount.name}`
        : bankAccount.name,
      value: bankAccount.bankAccountId,
      subLabel: formatIban(bankAccount.iban),
      alternativeLabels: [],
    }));
  }, [bankAccounts]);

  return (
    <ComboBoxInput
      name="entityId"
      label={t("account")}
      options={options}
      placeholder={t("select_bank_account")}
      disabled={disabled}
    />
  );
};
